/* eslint-disable complexity */
import { skipToken } from '@reduxjs/toolkit/query';
import { BenefitTypes } from '@strata/constants';
import { calcBidPercentage } from '@strata/helper-functions';
import { DateRangeInputFormik } from '@strata/react-date-time';
import { FormInputContainer } from '@strata/react-form-input-container';
import { DollarInputFormik, NumberInputFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { formatRateString } from '@strata/react-servicing-rate';
import { ErrorToast } from '@strata/react-toast-message';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { type FieldArrayRenderProps, useFormikContext } from 'formik';
import { useMemo } from 'react';

import { UnitPriceGrid } from 'components/shared/UnitPriceGrid';
import { type ClaimSubmissionFormSchema, ClaimUomOptions } from 'services/MedicalBillingConstants';
import {
    type DiagnosisCodeOption,
    type ProductCodeOption,
    usePostClaimEstimateCostQuery,
} from 'store/api/referenceApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

interface ServiceComponentProps {
    index: number;
    service: ClaimSubmissionFormSchema['services'][0];
    arrayHelpers: FieldArrayRenderProps;
    referralDetails?: ReferralDetails;
    productCodeDropdownOptions: ProductCodeOption[];
    serviceDiagnosesValues: DiagnosisCodeOption[];
}

export const ServiceComponent = ({
    index,
    service,
    referralDetails = undefined,
    productCodeDropdownOptions,
    serviceDiagnosesValues,
    arrayHelpers,
}: ServiceComponentProps) => {
    const formik = useFormikContext<ClaimSubmissionFormSchema>();

    const { billingQty, dateOfService, ndc, procedure } =
        formik.values[arrayHelpers.name as 'services'][index] ?? {};
    const priceIsFrozen = referralDetails?.priceIsFrozen;
    const isPharmacyBenefit = referralDetails?.benefit === BenefitTypes.PHARMACY;
    const isContractRate =
        formatRateString({ bidDecimal: referralDetails?.winningBid }) === 'Contract Rate';
    const dateOfServiceStart = dateOfService?.[0];

    const isPrimaryService = index === 0;
    const procedureIsJOrQCode =
        service.procedure?.code?.startsWith('J') || service.procedure?.code?.startsWith('Q');
    const ndcRequired = isPrimaryService || procedureIsJOrQCode;
    const isSameOidAsReferral = service.procedure?.oid === referralDetails?.productServiceOid;
    const useServiceChargeEstimate = procedureIsJOrQCode && isSameOidAsReferral;

    const procedureOptions = useMemo(
        () =>
            productCodeDropdownOptions.filter(
                (option) =>
                    (!isPrimaryService || option.oid === referralDetails?.productServiceOid) &&
                    option.codesystem !== 'NDC',
            ),
        [productCodeDropdownOptions, isPrimaryService, referralDetails?.productServiceOid],
    );

    const ndcOptions = useMemo(
        () =>
            productCodeDropdownOptions.filter(
                (option) =>
                    option.oid === service.procedure?.oid &&
                    option.codesystem === 'NDC' &&
                    (!isPrimaryService || option.isBrand === referralDetails?.isBrand),
            ),
        [
            productCodeDropdownOptions,
            isPrimaryService,
            service.procedure?.oid,
            referralDetails?.isBrand,
        ],
    );

    const { data: claimEstimateCost } = usePostClaimEstimateCostQuery(
        useServiceChargeEstimate &&
            dateOfServiceStart &&
            ndc &&
            procedure &&
            priceIsFrozen != null &&
            !isContractRate &&
            referralDetails?.priceSource &&
            referralDetails.referentPriceType
            ? {
                  productServiceOid: referralDetails.productServiceOid,
                  dateOfService: dateOfServiceStart,
                  referralStart: referralDetails.referralStart,
                  priceSource: referralDetails.priceSource,
                  priceIsFrozen,
                  qty: 1, // Use qty of one to avoid api call every time the qty changes
                  bidAmt: referralDetails.winningBid!,
                  referentPriceType: referralDetails.referentPriceType,
                  ndc: ndc.code,
                  hcpcsCode: procedure.code,
              }
            : skipToken,
    );
    const serviceChargeEstimate = claimEstimateCost?.claim_fact ?? 0;

    return (
        <FormInputContainer
            className="margin__bottom--large"
            closeable={!isPrimaryService}
            expandOnRender
            onRemoveContainerClick={() => {
                arrayHelpers.remove(index);
            }}
        >
            <div className="flex__gap--large ">
                <div style={{ width: '100%' }}>
                    <div className="sds_label font-semi-bold--uppercase margin__bottom--medium">
                        {`Service ${index + 1}`}
                    </div>
                    <DateRangeInputFormik
                        name={`${arrayHelpers.name}.${index}.dateOfService`}
                        label="Date(s) Of Service"
                        helperText="Single date range can be selected by entering same date for both."
                        maxDate={dayjs().endOf('day').toDate()}
                        required
                    />
                    <div className="flex__gap--medium">
                        <div style={{ flex: 1 }}>
                            <SuggestSingleSelectFormik
                                id={`${arrayHelpers.name}.${index}.procedure`}
                                label="Procedure"
                                required
                                values={procedureOptions}
                                onChange={(newVal) =>
                                    arrayHelpers.replace(index, {
                                        ...service,
                                        procedure: newVal,
                                        ndc:
                                            newVal?.code !== service.procedure?.code
                                                ? undefined
                                                : service.ndc,
                                        providerCharge:
                                            !isPrimaryService &&
                                            newVal?.oid === referralDetails?.productServiceOid
                                                ? Decimal.mul(
                                                      serviceChargeEstimate,
                                                      billingQty ?? 0,
                                                  )
                                                      .toDP(2)
                                                      .toNumber()
                                                : 0,
                                    })
                                }
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <TextInputFormik
                                id={`${arrayHelpers.name}.${index}.modifierCodes`}
                                label="Modifier Code(s)"
                                helperText="Enter up to 4 modifier codes separated by commas."
                            />
                        </div>
                    </div>
                    <SuggestSingleSelectFormik
                        id={`${arrayHelpers.name}.${index}.ndc`}
                        label="NDC"
                        required={ndcRequired}
                        createNewItemFromQuery={
                            index > 0 ? (query) => ({ name: query, code: query }) : undefined
                        }
                        values={ndcOptions}
                    />
                    <SuggestMultiSelectFormik
                        id={`${arrayHelpers.name}.${index}.diagnoses`}
                        label="Diagnoses"
                        helperText="4 additional diagnoses max."
                        required
                        values={serviceDiagnosesValues}
                        onChange={(newValues) => {
                            if (
                                !isPrimaryService ||
                                newValues.includes(formik.values.primaryDiagnosis)
                            ) {
                                arrayHelpers.replace(index, { ...service, diagnoses: newValues });
                            } else {
                                ErrorToast({
                                    message:
                                        'Primary diagnosis cannot be removed from first service.',
                                });
                            }
                        }}
                    />
                    <NumberInputFormik
                        id={`${arrayHelpers.name}.${index}.billingQty`}
                        label="Billing Quantity"
                        required
                        onChange={(e) => {
                            void (async () => {
                                const serviceBillingQty = e.target.value;
                                await formik.setFieldValue(
                                    `${arrayHelpers.name}.${index}.billingQty`,
                                    serviceBillingQty,
                                );
                                if (useServiceChargeEstimate) {
                                    const billingCharge = Decimal.mul(
                                        serviceChargeEstimate,
                                        serviceBillingQty,
                                    )
                                        .toDP(2)
                                        .toNumber();
                                    await formik.setFieldValue(
                                        `${arrayHelpers.name}.${index}.providerCharge`,
                                        billingCharge,
                                    );
                                }
                            })();
                        }}
                    />
                    <div className="flex__gap--large">
                        <div style={{ flex: 1 }}>
                            <NumberInputFormik
                                id={`${arrayHelpers.name}.${index}.dispensedQty`}
                                label="Dispensed Quantity"
                                required={ndcRequired}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <SuggestSingleSelectFormik
                                id={`${arrayHelpers.name}.${index}.dispensedQtyUom`}
                                label="Unit Of Measure"
                                required={ndcRequired}
                                values={ClaimUomOptions}
                            />
                        </div>
                    </div>
                    <DollarInputFormik
                        id={`${arrayHelpers.name}.${index}.providerCharge`}
                        label="Provider Charge"
                        disabled={useServiceChargeEstimate && !isContractRate}
                        required
                    />
                </div>
                <div className="sds_unit-price-grid">
                    {(isPrimaryService || isSameOidAsReferral) && !isContractRate && (
                        <UnitPriceGrid
                            priceFrozenMsg="Servicing unit price was locked at the servicing start date for the life of this referral."
                            referralDetails={referralDetails}
                            code={isPharmacyBenefit ? service.ndc?.code : service.procedure?.code}
                            codeRequired
                            priceDate={
                                priceIsFrozen ? referralDetails?.referralStart : dateOfServiceStart
                            }
                            currentBid={calcBidPercentage(referralDetails?.winningBid)}
                        />
                    )}
                </div>
            </div>
        </FormInputContainer>
    );
};
